import { inject, Injectable } from '@angular/core';
import { from, map, Observable, switchMap, tap } from 'rxjs';
import { EingangCtBatch, PsNavigationService } from '@ps-gui/claims-terminal-shared';
import { Navigate } from '@ps-gui/shared-utils';

import { CtBatchRedirectStoreService } from './ct-batch-redirect-store.service';
import { EingangCompletionBuilder } from './eingang-completion.builder';
import { ExtractionTrainingSubmissionService } from './extraction-training-submission.service';
import { CtBatchExtractionApiService } from '../api/ct-batch-extraction-api.service';
import { PersistDataService } from '../app-state/persist-data.service';
import { AuditZuordnungDto, CompleteEingangCt2Dto } from '../../models/interfaces';
import { GUTACHTEN_AUFTRAGSARTEN_STRINGS } from '@ps-gui/shared-core';

@Injectable({ providedIn: 'root' })
export class PsServerCtBatchTransmissionService {
  private readonly ctBatchExtractionApiService = inject(CtBatchExtractionApiService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly psNavigationService = inject(PsNavigationService);
  private readonly redirectStore = inject(CtBatchRedirectStoreService);
  private readonly completionBuilder = inject(EingangCompletionBuilder);
  private readonly trainingSubmission = inject(ExtractionTrainingSubmissionService);

  transmitAndRedirect(ctBatch: EingangCtBatch): Observable<unknown> {
    return from(this.completionBuilder.loadSnapshotAndBuildDto(ctBatch.metaData)).pipe(
      switchMap(({ dto }) => {
        const dtoWithAuditId: CompleteEingangCt2Dto = { ...dto };

        const altfallZuweisung = this.persistDataService.altfallZuweisung;

        if (altfallZuweisung) {
          if (altfallZuweisung.isLegacySystemAuftrag) {
            dtoWithAuditId.selectedAuditId = altfallZuweisung.auftragId;
          } else {
            dtoWithAuditId.selectedAuftragId = altfallZuweisung.auftragId;
          }
        }

        return this.ctBatchExtractionApiService
          .completeEingangCt2WithForwarding(ctBatch.id, dtoWithAuditId)
          .pipe(map((response) => ({ response, dto: dtoWithAuditId })));
      }),
      switchMap(({ response, dto }) => {
        const auftragsart = dto.consolidatedData.auftragsart;

        if (GUTACHTEN_AUFTRAGSARTEN_STRINGS.has(auftragsart)) {
          return from(this.persistDataService.reset()).pipe(
            tap(() => this.psNavigationService.navigateToGutachten(response.auftragIds[0])),
          );
        }

        if (auftragsart === 'ATTEST') {
          return from(this.persistDataService.reset()).pipe(
            tap(() => this.psNavigationService.navigateToDigideskTaskList()),
          );
        }

        const eseId = response.eseId;

        if (auftragsart === 'ESE' && eseId) {
          return from(this.persistDataService.reset()).pipe(
            tap(() => this.psNavigationService.navigateToDigideskEse(eseId)),
          );
        }

        return this.trainingSubmission.submitTrainingDataForDocuments().pipe(
          switchMap(() => from(this.persistDataService.reset())),
          tap(() => this.openAudits(response.auditZuordnungen)),
        );
      }),
    );
  }

  /**
   * Opens every forwarded audit: the first in the current tab (replace), the remaining ones in new
   * tabs. New tabs are opened first so they fire before the current tab starts navigating.
   */
  private openAudits(auditZuordnungen: AuditZuordnungDto[]): void {
    const [primary, ...rest] = auditZuordnungen;
    rest.forEach((zuordnung) =>
      Navigate.open(this.redirectStore.buildAuditRedirectUrl(zuordnung.auditId)),
    );
    if (primary) {
      Navigate.replace(this.redirectStore.buildAuditRedirectUrl(primary.auditId));
    }
  }
}
